import { useAuthHeaders } from "@src/frontend/hooks/useAuthHeaders";
import { useRestAPI } from "@src/frontend/hooks/useRestAPI";
import { useListDataScore } from "@src/frontend/stores/listDataScore";
import type { AgentModel, BrowserSessionModel } from "@src/generated/prisma/models";
import { Button, Flex, Input, Layout, Space, Tabs, Tag, Typography } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { styled } from "styled-components";

interface ScreenFrame { targetId: string; title: string; url: string; screenshot?: string }
type PageControl =
  | { type: 'CLICK'; x_ratio: number; y_ratio: number; button?: 'left' | 'right'; click_count?: number }
  | { type: 'SCROLL'; delta_x: number; delta_y: number }
  | { type: 'TYPE'; text: string } | { type: 'KEY'; key: string }
  | { type: 'NAVIGATE'; action: 'BACK' | 'FORWARD' | 'RELOAD' };

const WatchLayout = styled(Layout)`
  height: 100vh; padding: 0 24px 16px; overflow: hidden;
  .ant-tabs { min-height: 0; flex: 1; }
  .ant-tabs-content-holder, .ant-tabs-content, .ant-tabs-tabpane { height: 100%; min-height: 0; }
`;
const Preview = styled.div`
  height: 100%; overflow: auto; background: #111827; border-radius: 10px; padding: 12px;
  text-align: center; user-select: none;
  img { display: block; width: 100%; height: auto; cursor: crosshair; }
`;

export const ScreenWatch = () => {
  const location = useLocation();
  const { getAuthHeader } = useAuthHeaders();
  const [activeKey, setActiveKey] = useState('');
  const [screens, setScreens] = useState<Record<string, ScreenFrame>>({});
  const [status, setStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('connecting');
  const [retry, setRetry] = useState(0);
  const [text, setText] = useState('');
  const wsRef = useRef<WebSocket | null>(null);
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { session_id, agent_id } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return { session_id: params.get('session_id'), agent_id: params.get('agent_id') };
  }, [location.search]);

  useEffect(() => {
    if (!session_id || !agent_id) return;
    let disposed = false;
    setStatus('connecting');
    const connect = async () => {
      const headers = await getAuthHeader();
      if (!headers || disposed) return;
      const result = await fetch('/api/watch-ticket', {
        method: 'POST', headers, body: JSON.stringify({ session_id, agent_id }),
      }).then(res => res.json()) as { ok: boolean; data?: { ticket: string } };
      if (!result.ok || !result.data?.ticket) throw new Error('Unable to create watch ticket');
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${protocol}//${window.location.host}/ws/client/watch/${session_id}/${agent_id}?ticket=${encodeURIComponent(result.data.ticket)}`);
      wsRef.current = ws;
      ws.onopen = () => setStatus('connected');
      ws.onerror = () => setStatus('error');
      ws.onclose = () => { if (!disposed) setStatus('disconnected'); };
      ws.onmessage = event => {
        const message = JSON.parse(event.data) as ({ type: 'FRAME' } & ScreenFrame) | { type: 'TABS'; tabs: ScreenFrame[] };
        if (message.type === 'FRAME') setScreens(current => ({ ...current, [message.targetId]: { ...current[message.targetId], ...message } }));
        else setScreens(current => Object.fromEntries(message.tabs.map(tab => [tab.targetId, { ...current[tab.targetId], ...tab }])));
      };
    };
    void connect().catch(() => setStatus('error'));
    return () => { disposed = true; wsRef.current?.close(); wsRef.current = null; };
  }, [agent_id, getAuthHeader, retry, session_id]);

  const screenList = Object.values(screens);
  useEffect(() => {
    if (!screenList.some(screen => screen.targetId === activeKey)) setActiveKey(screenList[0]?.targetId ?? '');
  }, [activeKey, screenList]);

  const send = useCallback((targetId: string, control: PageControl) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify({ type: 'CONTROL', targetId, control }));
  }, []);
  const point = (event: React.MouseEvent<HTMLImageElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x_ratio: (event.clientX - rect.left) / rect.width, y_ratio: (event.clientY - rect.top) / rect.height };
  };

  const { listMap, getList } = useListDataScore(store => store);
  useRestAPI('/api/sessions'); useRestAPI('/api/agents');
  const { sessionName = '', agentName = '' } = useMemo(() => ({
    sessionName: getList<BrowserSessionModel>('/api/sessions').find(e => e.id === session_id)?.name,
    agentName: getList<AgentModel>('/api/agents').find(e => e.id === agent_id)?.name,
  }), [agent_id, getList, listMap, session_id]);
  const current = screens[activeKey];

  return <WatchLayout>
    <Flex align="center" justify="space-between">
      <Typography.Title level={3}>{agentName} - {sessionName}</Typography.Title>
      <Space><Tag color={status === 'connected' ? 'green' : status === 'connecting' ? 'blue' : 'red'}>{status}</Tag>
        {status !== 'connected' && <Button onClick={() => setRetry(value => value + 1)}>重新连接</Button>}
      </Space>
    </Flex>
    <Flex gap={8} style={{ marginBottom: 10 }}>
      {(['BACK', 'FORWARD', 'RELOAD'] as const).map((action, index) => <Button key={action} disabled={!current} onClick={() => current && send(current.targetId, { type: 'NAVIGATE', action })}>{['后退', '前进', '刷新'][index]}</Button>)}
      <Input value={text} onChange={event => setText(event.target.value)} placeholder="向当前焦点输入文本" onPressEnter={() => current && send(current.targetId, { type: 'TYPE', text })} />
      <Button disabled={!current || !text} onClick={() => current && send(current.targetId, { type: 'TYPE', text })}>输入</Button>
      <Button disabled={!current} onClick={() => current && send(current.targetId, { type: 'KEY', key: 'Enter' })}>Enter</Button>
      <Button disabled={!current} onClick={() => current && send(current.targetId, { type: 'KEY', key: 'Escape' })}>Esc</Button>
    </Flex>
    <Typography.Text type="secondary">直接点击、双击、右键或滚动预览画面，操作会发送到对应浏览器标签页。</Typography.Text>
    <Tabs hideAdd type="editable-card" activeKey={activeKey} onChange={setActiveKey}
      onEdit={targetId => { if (typeof targetId === 'string') wsRef.current?.send(JSON.stringify({ type: 'CLOSE_TAB', targetId })); }}
      items={screenList.map(screen => ({ key: screen.targetId, label: (screen.title || 'blank').slice(0, 18), children:
        <Preview onWheel={event => send(screen.targetId, { type: 'SCROLL', delta_x: event.deltaX, delta_y: event.deltaY })}>
          {screen.screenshot ? <img draggable={false} src={`data:image/jpeg;base64,${screen.screenshot}`}
            onClick={event => { const position = point(event); if (clickTimer.current) clearTimeout(clickTimer.current); clickTimer.current = setTimeout(() => send(screen.targetId, { type: 'CLICK', ...position }), 220); }}
            onDoubleClick={event => { if (clickTimer.current) clearTimeout(clickTimer.current); send(screen.targetId, { type: 'CLICK', ...point(event), click_count: 2 }); }}
            onContextMenu={event => { event.preventDefault(); send(screen.targetId, { type: 'CLICK', ...point(event), button: 'right' }); }} />
          : <Typography.Text style={{ color: '#94a3b8' }}>正在等待首帧…</Typography.Text>}
        </Preview> }))} />
  </WatchLayout>;
};
